import { selectInfoUser } from 'app/pages/App/slice/selectors';
import React, {
  createContext,
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { BP_TYPE } from 'utils/constants';
import { FilterFormValues, MAIN_TABS, TYPE_REPORT } from './components/Filter';
import moment from 'moment-timezone';
import { FormProvider, useForm } from 'react-hook-form';
import { LoadingContainer } from 'app/components/LoadingContainer';
import OverviewScreen from './components/OverviewScreen';
import DetailScreen from './components/DetailScreen';

export enum SCREEN {
  TRANSACTION_OVERVIEW,
  TRANSACTION_DETAIL,
}

export type PageState = {
  setPageLoading: Dispatch<SetStateAction<boolean>>;
  filterRef: MutableRefObject<FilterFormValues>;
  tableRef: MutableRefObject<HTMLDivElement | null>;
  anchorPointerRef?: MutableRefObject<HTMLDivElement | null>;
  setScreen: Dispatch<SetStateAction<SCREEN>>;
};

export const momentTz = moment().tz('Asia/Ho_Chi_Minh');

const DEFAULT_FILTER: FilterFormValues = {
  dateFrom: momentTz.clone().startOf('month'),
  dateTo: momentTz.clone(),
  documentNumber: undefined,
  transactionType: undefined,
  paymentMethod: undefined,
  page: 1,
  limit: 20,
  typeReport: TYPE_REPORT.OVERVIEW,
  dateFromList: momentTz.clone().startOf('month'),
  dateToList: momentTz.clone(),
  tab: MAIN_TABS.OVERVIEW,
};

export const PageStateContext = createContext<PageState>({
  filterRef: { current: DEFAULT_FILTER },
  tableRef: { current: null },
  anchorPointerRef: { current: null },
  setPageLoading: () => {},
  setScreen: () => {},
});

const TransactionHistoryOverview: React.FC = () => {
  const { replace } = useHistory();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const [screen, setScreen] = useState(
    queryParams.get('screen') === 'detail'
      ? SCREEN.TRANSACTION_DETAIL
      : SCREEN.TRANSACTION_OVERVIEW,
  );

  const infoUser = useSelector(selectInfoUser);
  const isProxyBuyer =
    (infoUser?.businessPartner as any)?.bpType === BP_TYPE.PROXY_BUYER;

  const filterForm = useForm<FilterFormValues>({
    defaultValues: DEFAULT_FILTER,
  });

  const filterRef = useRef(filterForm.getValues());
  const [pageLoading, setPageLoading] = useState(false);

  const tableRef = useRef<HTMLDivElement>(null);
  const anchorPointerRef = useRef<HTMLDivElement>(null);

  const pageState = useMemo<PageState>(
    () => ({
      filterRef,
      setPageLoading,
      tableRef,
      setScreen,
      anchorPointerRef,
    }),
    [],
  );

  useLayoutEffect(() => {
    if (isProxyBuyer) replace('/page-not-found');
  }, [isProxyBuyer, replace]);

  if (isProxyBuyer) return null;

  return (
    <PageStateContext.Provider value={pageState}>
      <LoadingContainer loading={pageLoading}>
        <FormProvider {...filterForm}>
          {screen === SCREEN.TRANSACTION_OVERVIEW ? (
            <OverviewScreen />
          ) : (
            <DetailScreen />
          )}
        </FormProvider>
      </LoadingContainer>
    </PageStateContext.Provider>
  );
};

export default TransactionHistoryOverview;
